import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy';
import LessonPlan from '@/models/LessonPlan';
import { Breakpoints } from '@/utils/Breakpoint';
import { formatLessonPlanTime } from '@/utils/lesson-plan/formatLessonPlanTime';

import LessonPlanInlineForm from './LessonPlanInlineForm';

export type LessonPlanCardProps = {
  lessonPlan: LessonPlan;
  index: number;
  time: number;
  onDelete: (lessonPlanId: number) => Promise<void>;
  onUpdate?: (params: {
    lessonPlanId: number;
    data: Partial<LessonPlan>;
  }) => Promise<LessonPlan>;
  isUpdating?: boolean;
  isDeleting?: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
  isReordering?: boolean;
  children?: ReactNode;
};

export default function LessonPlanCard({
  lessonPlan,
  index,
  time,
  onDelete,
  onUpdate,
  isUpdating,
  isDraggable,
  isDragging,
  isReordering,
  isDeleting,
  children,
}: LessonPlanCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });
  const [isEditing, setIsEditing] = useState(false);

  const dragId = `lessonPlan-${lessonPlan.id}`;

  if (isEditing) {
    return (
      <CourseAdminCard.DraggableInlineForm
        id={dragId}
        index={index}
        isDraggable={isDraggable}
      >
        <LessonPlanInlineForm
          defaultValues={{ name: lessonPlan.name }}
          onSubmit={async data => {
            await onUpdate?.({ lessonPlanId: lessonPlan.id, data });
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
          isLoading={isUpdating}
        />
      </CourseAdminCard.DraggableInlineForm>
    );
  }

  const dragProps = isDraggable
    ? {
        isDraggable: true as const,
        id: dragId,
        index,
        disabled: isReordering,
      }
    : { isDraggable: false as const };

  return (
    <CourseAdminCard
      variant="block"
      isDeleting={isDeleting}
      isDragging={isDragging}
      className="mb-6"
      {...dragProps}
    >
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Order value={lessonPlan.order} />
          <CourseAdminCard.Title>{lessonPlan.name}</CourseAdminCard.Title>
          <ConditionalRenderer condition={time > 0}>
            <CourseAdminCard.Badges>
              <Badge color="success" size={isXl ? 'md' : 'sm'}>
                {t('blockTotalTime', { time: formatLessonPlanTime(time) })}
              </Badge>
            </CourseAdminCard.Badges>
          </ConditionalRenderer>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {onUpdate && (
            <CourseAdminCard.EditButton onClick={() => setIsEditing(true)} />
          )}
          <CourseAdminCard.MoreActions>
            <CourseAdminCard.MoreActions.Delete
              confirm
              onClick={() => onDelete(lessonPlan.id)}
            />
          </CourseAdminCard.MoreActions>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
      <CourseAdminCard.NestedList>{children}</CourseAdminCard.NestedList>
    </CourseAdminCard>
  );
}
